import { fetchSearch } from '@/search/searchService'
import { toRecommendationModel, type RecommendationModel } from '@/browse/RecommendationRenderer'
import { createCloudQueue } from '@/cloudQueue/cloudQueueService'
import type { CloudQueueController } from '@/cloudQueue/CloudQueueController'
import { ctx } from '@/util/context'
import type { EntityUnion } from '@/service/fragments/entity'

const { target, errorHandler } = ctx('voice')
void target

export interface PlaySearchDeps {
    readonly cloudQueueController: CloudQueueController
}

/**
 * Resolves a search query into a playable entity and starts playback. Used by
 * the Siri / Shortcuts bridge ("Play Bluey on Bullfrog") and the
 * `bullfrog:///play?q=...` deep link. Mirrors the click flow on a
 * RecommendationRenderer card but skips navigation — the user said "play",
 * so we play and stay wherever we are.
 *
 * Returns the model that was played, or `null` if no playable result.
 */
export async function playSearch(
    query: string,
    deps: PlaySearchDeps
): Promise<RecommendationModel | null> {
    const trimmed = query.trim()
    if (!trimmed) return null

    // Single TopResults specification covers album/artist/playlist/track/
    // station/podcast — the firefly ranker hands back the best match first.
    const result = await fetchSearch({ query: trimmed }, [
        {
            label: 'TopResults',
            types: [
                'album',
                'artist',
                'playlist',
                'track',
                'station',
                'podcast_show',
                'podcast_episode',
            ],
            limit: 5,
        },
    ])

    const edges = result.specificationSearch?.result?.[0]?.edges ?? []
    let model: RecommendationModel | null = null
    for (const edge of edges) {
        const candidate = toRecommendationModel(edge.node as EntityUnion)
        if (candidate?.playParams) {
            model = candidate
            break
        }
    }
    if (!model?.playParams) return null

    const queueResult = await createCloudQueue({ playParams: model.playParams })
    deps.cloudQueueController.setQueue({ playParams: model.playParams, queueResult })
    return model
}

let installedDeps: PlaySearchDeps | null = null

/**
 * Wires the global `bullfrog.playSearch(query)` entry point used by the iOS
 * Siri bridge. The native shell calls this directly via WKWebView's
 * `evaluateJavaScript` once the SPA has booted; if the shell calls before the
 * SPA is ready, the call is queued and replayed on install.
 */
export function installPlaySearchBridge(deps: PlaySearchDeps): void {
    installedDeps = deps
    const w = globalThis as VoiceWindow
    w.bullfrog ??= {}
    w.bullfrog.playSearch = (query: string) => {
        playSearch(query, deps).catch(errorHandler)
    }
    // Drain any queries the shell sent before the SPA was ready.
    const pending = w.bullfrog.__pendingPlaySearch
    if (pending && pending.length > 0) {
        w.bullfrog.__pendingPlaySearch = []
        for (const q of pending) {
            playSearch(q, deps).catch(errorHandler)
        }
    }
}

/** Test/debug helper — returns the dependencies installed via the bridge. */
export function getInstalledPlaySearchDeps(): PlaySearchDeps | null {
    return installedDeps
}

interface VoiceWindow {
    bullfrog?: {
        playSearch?: (query: string) => void
        __pendingPlaySearch?: string[]
    }
}
